import { z } from "zod";
import { env, createLoader, withLogging, ConfigSource } from "@biggeo/config";

export const envSchema = z.object({
  VITE_XGRID_KEY: z.string().default("not-specified"),
  VITE_DISABLE_AUTH: env.bool().default("false"),
  VITE_STUDIO_SERVER_URL: z.string().default("https://datascape.biggeo.com"),
  VITE_DISABLE_REDUX_LOGGER: env.bool().default("false"),
  VITE_APP_ENV: z.string().default("development"),
  VITE_MAPBOX_TOKEN: z
    .string()
    .default(
      "pk.eyJ1IjoiY2NhcnRoIiwiYSI6ImNsb3E1NTlweDBkbHMyaW9iaXRrNTZ1ZmIifQ.J95n-vaI-pyGcO8Hd082xQ",
    ),
  VITE_DATASCAPE_LOG_ROCKET_ID: z.string().default(""),
});

export type EnvConfig = z.infer<typeof envSchema>;

const safeFields: (keyof EnvConfig)[] = [
  "VITE_XGRID_KEY",
  "VITE_DISABLE_AUTH",
  "VITE_STUDIO_SERVER_URL",
  "VITE_DISABLE_REDUX_LOGGER",
  "VITE_APP_ENV",
  "VITE_MAPBOX_TOKEN",
  "VITE_DATASCAPE_LOG_ROCKET_ID",
];

const sources = (): ConfigSource[] => [
  { name: "env", values: import.meta.env },
  ...(typeof window !== "undefined" && window.ENV_CONFIG
    ? [{ name: "runtime", values: window.ENV_CONFIG }]
    : []),
];

const loader = createLoader(
  envSchema,
  withLogging("bg-spa-frontend", envSchema, safeFields),
);

const loadEnvConfig = (): EnvConfig => loader(sources());

// Match existing API for backwards compatibility
export const studioUrlFromEnv = (): string =>
  loadEnvConfig().VITE_STUDIO_SERVER_URL;
export const appEnvFromEnv = (): string => loadEnvConfig().VITE_APP_ENV;
export const xgridKeyFromEnv = (): string => loadEnvConfig().VITE_XGRID_KEY;
export const disableReduxLoggerFromEnv = (): boolean =>
  loadEnvConfig().VITE_DISABLE_REDUX_LOGGER;
export const mapboxTokenFromEnv = (): string =>
  loadEnvConfig().VITE_MAPBOX_TOKEN;
export const logRocketIDFromEnv = (): string =>
  loadEnvConfig().VITE_DATASCAPE_LOG_ROCKET_ID;

declare global {
  interface Window {
    ENV_CONFIG?: {
      VITE_XGRID_KEY?: string;
      VITE_DISABLE_AUTH?: string;
      VITE_STUDIO_SERVER_URL?: string;
      VITE_DISABLE_REDUX_LOGGER?: string;
      VITE_APP_ENV?: string;
      VITE_MAPBOX_TOKEN?: string;
      VITE_DATASCAPE_LOG_ROCKET_ID?: string;
    };
  }
}

